import { useCallback, useEffect, useMemo } from "react";
import {
  IconNMRHistoryEntry,
  IconNMRHistoryEntryFilters,
  IconNMRHistoryEntryFiltersTranslator,
  iconNMRHoldersConstants,
  iconNMRStatusConstants,
} from "../../../api/IconNMR";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { NotAvailable, NotSet } from "../../../common/misc/UIconstants";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import styles from "../../../common/tables/GenericVirtualizedTable/commons.module.css";
import { DataSource, DataSourceFilters, dataSourceConstants } from "../../../api/DataSource";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import TableView from "../../../common/panels/TableView/TableView";
import { useUnpaginateOrdered } from "../../../api/BaseEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

const calculateDuration = (completionTime?: string, startTime?: string) => {
  if (!completionTime || !startTime) return NotAvailable;
  let end: any = new Date(completionTime);
  let start: any = new Date(startTime);
  let diff = Math.abs(end - start);
  let hours = Math.floor(diff / 1000 / 60 / 60);
  let min = Math.floor(diff / 1000 / 60) - hours * 60;
  let s = Math.floor(diff / 1000) - hours * 60 * 60 - min * 60;
  return `${hours < 10 ? "0" + hours : hours}:${min < 10 ? "0" + min : min}:${s < 10 ? "0" + s : s}`;
};

export const switchPersonsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<IconNMRHistoryEntryFilters>>[] | null | undefined
): SortState<InferSortByOptions<IconNMRHistoryEntryFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "ID":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-id",
          property: sort.property,
        };

      case "START_TIME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-startTime",
          property: sort.property,
        };

      case "COMPLETION_TIME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-completionTime",
          property: sort.property,
        };

      case "USER":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-user",
          property: sort.property,
        };

      case "HOLDER":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-holder",
          property: sort.property,
        };

      case "DISK_UNIT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-diskUnit",
          property: sort.property,
        };

      case "EXPNO":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-expno",
          property: sort.property,
        };

      case "DATA_SOURCE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-dataSource",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-id",
          property: "ID",
        };
    }
  });
};

// const defaultFilter: IconNMRHistoryEntryFilters = { orderBy: "START_TIME_DESC" };

export const useIconNMRHistoryTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"iconNMRHistoryEntry">) => {
  const {
    data: dataSources,
    status,
    fetchStatus,
    error,
  } = useUnpaginateOrdered<DataSource, DataSourceFilters>(dataSourceConstants.resource, {
    types: ["IconNMR"],
    enabled: true,
  });

  const defaults: ColumnsSettings<IconNMRHistoryEntry> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id },
      "default-startTime": { pos: 1, active: true, header: fieldLabels.startTime },
      "default-completionTime": { pos: 2, active: true, header: fieldLabels.completionTime },
      "default-holder": { pos: 3, active: true, header: fieldLabels.holder },
      "default-dataset": { pos: 4, active: true, header: fieldLabels.datasetName },
      "default-expno": { pos: 5, active: true, header: fieldLabels.experimentNo },
      "default-experiment": { pos: 6, active: true, header: fieldLabels.experimentName },
      "default-duration": { pos: 7, active: true, header: fieldLabels.duration },
      "default-originatorTitle": { pos: 8, active: true, header: fieldLabels.originatorTitle },
      "default-sampleLoad": { pos: 11, active: true, header: fieldLabels.sampleLoad },
      "default-lock": { pos: 12, active: true, header: fieldLabels.lock },
      "default-shim": { pos: 13, active: true, header: fieldLabels.shim },
      "default-acquisition": { pos: 14, active: true, header: fieldLabels.acquisition },
      "default-processing": { pos: 15, active: true, header: fieldLabels.processing },
      "default-remarks": { pos: 16, active: true, header: fieldLabels.remarks },
      "default-dataSource": { pos: 17, active: false, header: fieldLabels.dataSource },
      "default-user": { pos: 18, active: false, header: fieldLabels.user },
      "default-diskUnit": { pos: 19, active: false, header: fieldLabels.diskUnit },
      "default-statusInformationForATM": { pos: 20, active: false, header: fieldLabels.statusInformationForATM },
      "default-rotation": { pos: 21, active: false, header: fieldLabels.rotation },
      "default-tubeId": { pos: 22, active: false, header: fieldLabels.tubeId },
      "default-analysisResult": { pos: 23, active: false, header: fieldLabels.analysisResult },
      "default-analysisOutput": { pos: 24, active: false, header: fieldLabels.analysisOutput },
      "default-sBase": { pos: 25, active: false, header: fieldLabels.sBase },
      "default-setupRow": { pos: 26, active: false, header: fieldLabels.setupRow },
      "default-consistency": { pos: 27, active: false, header: fieldLabels.consistency },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults = useMemo(() => {
    let def: TableTabsDict<IconNMRHistoryEntry, IconNMRHistoryEntryFilters, {}> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: {
            sortBy: [
              {
                property: "START_TIME",
                sortDirection: "DESC",
              },
            ],
          },
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    };
    if (dataSources) {
      dataSources.forEach((d, i) => {
        if (!!d.name && d.enabled)
          def[`dataSource-${d.id}`] = {
            tabId: `dataSource-${d.id}`,
            type: "fixed",
            label: d.name ?? d.name,
            title: d.name ?? d.name,
            icon: dataSourceConstants.icon,
            align: "left",
            xPos: 2 + i,
            settings: {
              columnSettings: {},
              columnWidths: {},
              filters: {
                dataSourceIds: [d.id],
                sortBy: [
                  {
                    property: "START_TIME",
                    sortDirection: "DESC",
                  },
                ],
              },
              sidebarFilters: {
                dataSource: [d],
              },
            },
            forcedSettings: {
              columnSettings: {},
              columnWidths: {},
              filters: { dataSourceIds: [d.id] },
              sidebarFilters: {},
            },
          };
      });
    }
    return def as TableTabsDict<IconNMRHistoryEntry, IconNMRHistoryEntryFilters, {}>;
  }, [dataSources]);

  return { defaults, tabStoreDefaults, status, fetchStatus, error };
};

export const useIconNMRHistoryEntriesTableColumns = ({
  sort,
  setSort,
  fieldLabels,
}: UseEntityTableProps<"iconNMRHistoryEntry">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<IconNMRHistoryEntry> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.startTime,
        id: "default-startTime",
        accessor: (row) => <DateTimeRenderer date={row.startTime} includeElapsed={false} />,
        minWidth: 120,
        width: 180,
        align: "left",
        ...getAsyncSortingFn("START_TIME", sort, setSort),
      },
      {
        Header: fieldLabels.completionTime,
        id: "default-completionTime",
        accessor: (row) => <DateTimeRenderer date={row.completionTime} includeElapsed={false} />,
        minWidth: 120,
        width: 180,
        align: "left",
        ...getAsyncSortingFn("COMPLETION_TIME", sort, setSort),
      },
      {
        Header: fieldLabels.duration,
        id: "default-duration",
        accessor: (row) => calculateDuration(row.completionTime, row.startTime),
        minWidth: 120,
        width: 180,
        align: "left",
      },
      {
        Header: fieldLabels.experimentName,
        id: "default-experiment",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.experimentName ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.experimentNo,
        id: "default-expno",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.experimentNo ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("EXPNO", sort, setSort),
      },
      {
        Header: fieldLabels.user,
        id: "default-user",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.user ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 150,
        align: "left",
        ...getAsyncSortingFn("USER", sort, setSort),
      },
      {
        Header: fieldLabels.holder,
        id: "default-holder",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.holder ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
        ...getAsyncSortingFn("HOLDER", sort, setSort),
      },
      {
        Header: fieldLabels.datasetName,
        id: "default-dataset",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.datasetName ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.acquisition,
        id: "default-acquisition",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.acquisition ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
      },
      {
        Header: fieldLabels.originatorTitle,
        id: "default-originatorTitle",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.originatorTitle ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.diskUnit,
        id: "default-diskUnit",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.diskUnit ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("DISK_UNIT", sort, setSort),
      },
      {
        Header: fieldLabels.statusInformationForATM,
        id: "default-statusInformationForATM",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.statusInformationForATM ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.rotation,
        id: "default-rotation",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.rotation ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.lock,
        id: "default-lock",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.lock ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.shim,
        id: "default-shim",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.shim ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.processing,
        id: "default-processing",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.processing ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.remarks,
        id: "default-remarks",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.remarks ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.sampleLoad,
        id: "default-sampleLoad",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.sampleLoad ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.tubeId,
        id: "default-tubeId",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.tubeId ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.analysisResult,
        id: "default-analysisResult",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.analysisResult ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.analysisOutput,
        id: "default-analysisOutput",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.analysisOutput ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.sBase,
        id: "default-sBase",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.sBase ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.setupRow,
        id: "default-setupRow",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.setupRow ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.consistency,
        id: "default-consistency",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.consistency ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.dataSource,
        id: "default-dataSource",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>
              <LucideIcon name={dataSourceConstants.icon} /> {row.dataSource.name}
            </span>
          </div>
        ),
        minWidth: 120,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("DATA_SOURCE", sort, setSort),
      },
    ];

    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.startTime,
    fieldLabels.completionTime,
    fieldLabels.duration,
    fieldLabels.experimentName,
    fieldLabels.experimentNo,
    fieldLabels.user,
    fieldLabels.holder,
    fieldLabels.datasetName,
    fieldLabels.acquisition,
    fieldLabels.originatorTitle,
    fieldLabels.diskUnit,
    fieldLabels.statusInformationForATM,
    fieldLabels.rotation,
    fieldLabels.lock,
    fieldLabels.shim,
    fieldLabels.processing,
    fieldLabels.remarks,
    fieldLabels.sampleLoad,
    fieldLabels.tubeId,
    fieldLabels.analysisResult,
    fieldLabels.analysisOutput,
    fieldLabels.sBase,
    fieldLabels.setupRow,
    fieldLabels.consistency,
    fieldLabels.dataSource,
    sort,
    setSort,
  ]);
  return { columns };
};

export const IconNMRHistoryTableWrapper = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"iconNMRHistoryEntry">) => {
  const { defaults, tabStoreDefaults, status, fetchStatus, error } = useIconNMRHistoryTableDefaults({ fieldLabels });

  if (!tabStoreDefaults)
    return (
      <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
        Fetching IconNMR instruments...
      </LoadingWrapper>
    );
  return (
    <IconNMRHistoryTable
      entityTypeId={entityTypeId}
      defaults={defaults}
      tabStoreDefaults={tabStoreDefaults}
      entityApi={entityApi}
      entityConstants={entityConstants}
      fieldLabels={fieldLabels}
      permissions={permissions}
      routes={routes}
    />
  );
};

export const IconNMRHistoryTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  defaults,
  tabStoreDefaults,
}: {
  defaults: ColumnsSettings<IconNMRHistoryEntry>;
  tabStoreDefaults: TableTabsDict<IconNMRHistoryEntry, IconNMRHistoryEntryFilters, {}>;
} & EntityTableProps<"iconNMRHistoryEntry">) => {
  const { resultsCount, onCountChange } = useGenericVirtualizedTable<IconNMRHistoryEntry>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<IconNMRHistoryEntry, IconNMRHistoryEntryFilters, {}>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchPersonsDefaultSortState,
    dispatchTabStore,
  });
  const { columns } = useIconNMRHistoryEntriesTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  const queryClient = useQueryClient();

  const refetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [iconNMRStatusConstants.resource] });
    queryClient.invalidateQueries({ queryKey: [iconNMRHoldersConstants.resource] });
    queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
  }, [entityConstants.resource, queryClient]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchData();
    }, 60000);
    return () => clearInterval(interval);
  }, [refetchData]);

  return (
    <TableView style={{ marginTop: 0, paddingTop: 0 }}>
      <TableView.Body>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
            showAddTab={false}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<IconNMRHistoryEntry, IconNMRHistoryEntryFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={IconNMRHistoryEntryFiltersTranslator}
                showAlways
              />

              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />

              <div className="flex align-center gap-5">
                <span style={{ color: "var(--gray-300)", paddingRight: "5px" }}>|</span>
                <span style={{ color: "var(--gray-500)", whiteSpace: "nowrap" }}>
                  Results with <LucideIcon name="filter" />:
                </span>
                <span className="badge">{resultsCount ?? NotAvailable}</span>
                <span style={{ color: "var(--gray-300)", paddingLeft: "5px" }}>|</span>
              </div>
              <button onClick={refetchData} className="btn btn-ghost-primary btn-xs flex align-center gap-5">
                <LucideIcon name="refresh-cw" />
                Refresh
              </button>
            </EntityTable.Controls>
            <EntityTable.Body<IconNMRHistoryEntry, IconNMRHistoryEntryFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              loading={tabsLoading}
              onRowClick={() => {}}
              linkTo={() => "#"}
              disableCheckboxes
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
